import { openLink, openTelegramLink } from '@telegram-apps/sdk-react';
import clsx from 'clsx';
import { motion, useAnimation } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@common/ui/buttons/Button';
import { safeTma } from '@common/utils/safeTma';
import {
  isTelegramLink,
  toOpenableTelegramLink,
} from '@common/utils/telegramLinks';

import { useSwipeAnimation } from '@entities/dating/hooks/useSwipeAnimation';

import { type Advertising } from '@services/FeedAdService';
import { type FeedAdvertising } from '@services/FeedService';
import {
  usePostFeedAdViewById,
  usePostFeedAdViewClickById,
} from '@services/hooks/FeedAdService';

import styles from './AdvertisingCard.module.scss';

export interface AdvertisingCardProps {
  profile: FeedAdvertising | undefined;
  onSkip: (profile: FeedAdvertising) => void;
  data: Advertising;
  style?: React.CSSProperties;
  isActive: boolean;
}

export const AdvertisingCard = ({
  profile,
  onSkip,
  style,
  data,
  isActive,
}: AdvertisingCardProps) => {
  const hasPostedRef = useRef(false);

  const [viewId, setViewId] = useState<number | null>(null);

  const controls = useAnimation();

  const {
    trigger: triggerPostFeedAdViewById,
    isMutating: isMutatingPostFeedAdViewById,
  } = usePostFeedAdViewById({
    id: data.ad.id,
  });

  const { trigger: triggerPostFeedAdViewClickById } =
    usePostFeedAdViewClickById();

  useEffect(() => {
    if (isActive && !hasPostedRef?.current) {
      triggerPostFeedAdViewById().then((response) => {
        setViewId(response.view_id);
        hasPostedRef.current = true;
      });
    }
  }, [isActive, triggerPostFeedAdViewById]);

  const {
    dislikeGradientRef,
    handleDrag,
    handleDragEnd,
    likeGradientRef,
    opacity,
    rotate,
    x,
  } = useSwipeAnimation({ onLike: onSkip, onSkip, profile });

  const handleButtonClick = () => {
    const link = data.ad.action_link;

    if (viewId) {
      triggerPostFeedAdViewClickById({ id: viewId });
    }

    // The click is already recorded above, so a dead Telegram transport must
    // not turn this into an uncaught error. Fall back to a plain new-context
    // open rather than letting the ad silently do nothing — the advertiser
    // was charged for this click.
    const isTgLink = isTelegramLink(link);
    const targetUrl = isTgLink ? toOpenableTelegramLink(link) : link;

    const opened = isTgLink
      ? safeTma('ad.openTelegramLink', () => openTelegramLink(targetUrl))
      : safeTma('ad.openLink', () => openLink(targetUrl));

    // Unlike the task handler, this one is synchronous with no awaits ahead
    // of it, so the click's transient user activation is still intact and
    // window.open is a genuine fallback rather than a hope.
    if (!opened) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const titleText = data.ad.title.replace(/\\n/g, '\n');

  return (
    <motion.div
      data-test-id="advertising-card-container"
      className={styles.advertisingCard}
      style={{
        x,
        rotate,
        opacity,
        ...style,
      }}
      drag="x"
      animate={controls}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
    >
      <div className={styles.gradient} data-type="default" />

      <div ref={likeGradientRef} className={styles.gradient} data-type="like" />
      <div
        ref={dislikeGradientRef}
        className={styles.gradient}
        data-type="dislike"
      />

      <div className={styles.imageContainer}>
        <img
          src={data.ad.picture_url}
          alt="Реклама"
          data-test-id="advertising-card-image"
        />
      </div>

      <div className={clsx(styles.content)}>
        <div className={styles.title}>{titleText}</div>

        <div className={styles.description}>
          <span>{data.ad.description}</span>
        </div>

        <Button
          testId="advertising-card-action-button"
          variant="premiumGradient"
          className={styles.premiumBtn}
          loading={isMutatingPostFeedAdViewById}
          disabled={!viewId}
          onClick={handleButtonClick}
        >
          {data.ad.action_text}
        </Button>
      </div>
    </motion.div>
  );
};
